<template>
  <ContainerLayout>
    <div
      class="tw:min-h-(--tw-min-height-screen-minus-header-and-footer) tw:flex-col tw:flex tw:w-full tw:md:max-w-(--tw-breakpoint-lg) tw:lg:max-w-(--tw-breakpoint-xl) tw:mr-auto tw:ml-auto tw:mb-8"
    >
      <AppMenu :data="tabs" :loading="false" class="tw:w-full" />
      <router-view />
    </div>
  </ContainerLayout>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted, type Ref, type ComputedRef } from 'vue'
import { useRouter } from 'vue-router'

import AppMenu from '@/components/appMenu/AppMenu.vue'
import ContainerLayout from '@/layouts/ContainerLayout.vue'

import { useSessionValues } from '@/composables/useSessionValues'
import type { OrganisationRole } from '@/types'

/** Settings navigation tab */
interface SettingsTab {
  label: string
  route: string
  testId?: string
}

/** Admin-only route names */
type AdminRouteName = 'settings-team-settings'

const router = useRouter()
const { userRole } = useSessionValues()

/** Admin-only route names for access control */
const adminRoutes: AdminRouteName[] = ['settings-team-settings']

/** Settings tabs for admin users */
const adminTabs: Ref<SettingsTab[]> = ref([
  {
    label: 'organization_settings_tab',
    route: '/settings/team-settings',
    testId: 'settings-team-settings',
  },
  {
    label: 'form.info.tab_profile_settings',
    route: '/settings/profile-settings',
    testId: 'settings-profile-settings',
  },
  {
    label: 'form.info.tab_language_currency',
    route: '/settings/locale-settings',
    testId: 'settings-locale-settings',
  },
])

/** Settings tabs for non-admin users */
const memberTabs: Ref<SettingsTab[]> = ref([
  {
    label: 'form.info.tab_profile_settings',
    route: '/settings/profile-settings',
    testId: 'settings-profile-settings',
  },
  {
    label: 'form.info.tab_language_currency',
    route: '/settings/locale-settings',
    testId: 'settings-locale-settings',
  },
])

/**
 * Tabs to display based on user role
 * Admin sees all tabs, members see only profile and locale
 */
const tabs: ComputedRef<SettingsTab[]> = computed(() =>
  userRole.value === 'admin' ? adminTabs.value : memberTabs.value
)

/**
 * Check if user is trying to access admin-only route
 * @returns True if current route requires admin access
 */
const isAccessingAdminRoute = (): boolean => {
  const currentRouteName = router.currentRoute.value.name
  return adminRoutes.includes(currentRouteName as AdminRouteName)
}

/**
 * Redirect non-admin users away from admin routes
 */
const redirectIfUnauthorized = (): void => {
  if (userRole.value !== 'admin' && isAccessingAdminRoute()) {
    router.push('/settings/profile-settings')
  }
}

onMounted(() => {
  redirectIfUnauthorized()
})

/**
 * Watch for role changes and redirect if needed
 * Handles cases where role changes while on an admin route
 */
watch(userRole, (role: OrganisationRole) => {
  if (role !== 'admin' && isAccessingAdminRoute()) {
    router.push('/settings/profile-settings')
  }
})
</script>
